import { useCallback, useEffect, useMemo, useState } from "react";

import type { JobCache } from "../../cache";
import { Config, JobConfig, mergeConfig } from "../../config";
import { useConfigFile } from "../../list";
import { connectToRunlyApi } from "../job-channel";
import ResultsLog from "../results/results-log";
import Execution, { Events, Run } from ".";

export interface RunAction {
	onComplete?: () => void;
	cache: JobCache;
	config: Config;
}

interface ConfigError extends Error {
	name: string;
	message: string;
	stack?: string;
	contents?: string;
}

const useRunner = ({
	cache,
	config: inputConfig
}: RunAction): { config: Config; run?: Run; error: ConfigError } => {
	const [, signal] = useState(0);

	const { config: loadedConfig, error: configError } = useConfigFile(
		inputConfig,
		cache
	);
	const loadJob = useCallback(name => cache.loadJob(name), [cache]);

	// get the job
	const job = useMemo(() => {
		if (loadedConfig) {
			const jobConfig = loadedConfig.job;

			let jobType = loadedConfig.job as string;

			if ((jobConfig as JobConfig).type) {
				jobType = (jobConfig as JobConfig).type;
			}

			return loadJob(jobType);
		}
	}, [loadJob, loadedConfig]);

	// merge the root default config with the job's default config with the passed-in config
	const config = useMemo<Config>(() => {
		if (job) {
			return mergeConfig(job.defaultConfig, loadedConfig);
		}
	}, [job, loadedConfig]);

	const execution = useMemo(
		() => (job && config ? new Execution(job, config) : undefined),
		[job, config]
	);

	// run the job and collect results
	useEffect(() => {
		async function doit() {
			console.log("doit");
			if (config.runlyApi?.token && config.runlyApi.token.length > 0) {
				console.log("connect");
				await connectToRunlyApi(execution);
			}

			await execution.start();
		}

		console.log("useRunner/useEffect");
		console.log(execution === undefined ? "no execution" : "execution exists");
		console.log(config === undefined ? "no config" : "config exists");

		if (execution) {
			const unsubscribe = execution.onAny(() => {
				signal(i => i + 1);
			});
			let resultsLog: ResultsLog;

			if (config.results.filePath !== undefined) {
				resultsLog = new ResultsLog(execution);
				execution.on(Events.Completed, _ => {
					resultsLog.write();
				});
			}

			void doit();

			return () => {
				if (unsubscribe) {
					unsubscribe();
				}
			};
		}
	}, [execution, config]);

	return { config, error: configError, run: execution };
};

export default useRunner;
